/** Small formatters shared by the plausibility pages. */

/**
 * Below 0.01% a fixed two decimal places renders every small posterior as
 * "0.00%", which reads as impossible rather than unlikely and makes values a
 * factor of ten apart look identical. Small values keep two significant
 * figures instead.
 */
export const formatPercent = (p: number | undefined): string => {
	if (p === undefined) return "—";
	const percent = p * 100;
	if (percent > 0 && percent < 0.01) return `${percent.toPrecision(2)}%`;
	return `${percent.toFixed(percent < 1 ? 2 : 1)}%`;
};

export const formatLogOdds = (x: number | undefined): string =>
	x === undefined ? "—" : x.toFixed(2);

/**
 * A small probability as a natural frequency, which people read far more
 * reliably than a percentage with leading zeros. Above 1-in-2 the phrasing
 * stops helping, so the caller shows the percentage alone.
 */
export const formatOneIn = (p: number | undefined): string => {
	if (p === undefined || p <= 0 || p >= 0.5) return "—";
	const denominator = 1 / p;
	const magnitude = 10 ** Math.max(0, Math.floor(Math.log10(denominator)) - 1);
	return `1 in ${(Math.round(denominator / magnitude) * magnitude).toLocaleString()}`;
};

/**
 * A log likelihood ratio as the factor it applies to the odds. It is not a
 * probability and has no percentage form: the evidence multiplied the odds
 * by this much, or divided them by it.
 */
export const formatEvidenceFactor = (logLr: number | undefined): string => {
	if (logLr === undefined) return "—";
	const factor = Math.exp(Math.abs(logLr));
	if (factor < 1.05) return "no effect";
	const rounded =
		factor >= 100 ? Math.round(factor) : Number(factor.toFixed(1));
	return `${logLr < 0 ? "÷" : "×"}${rounded.toLocaleString()}`;
};

/**
 * A probability as people read it: the percentage, plus the natural frequency
 * when the value is small enough for that to add anything.
 */
export const formatProbability = (p: number | undefined): string => {
	const percent = formatPercent(p);
	const oneIn = formatOneIn(p);
	return oneIn === "—" ? percent : `${percent} · ${oneIn}`;
};

export const probabilityFromLogOdds = (
	logOdds: number | undefined,
): number | undefined =>
	logOdds === undefined ? undefined : 1 / (1 + Math.exp(-logOdds));

/** Probability implied by log odds, for reading a prior row at a glance. */
export const logOddsToPercent = (logOdds: number): string =>
	formatPercent(probabilityFromLogOdds(logOdds));

export const formatWhen = (value: Date | string | undefined): string =>
	value ? new Date(value).toLocaleString() : "—";

export const formatSeverityChange = (
	from: number | undefined,
	to: number | undefined,
): string => {
	if (from === undefined) return "—";
	if (to === undefined || to === from) return String(from);
	return `${from} → ${to}`;
};
